import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { Close, Update, Save } from '@mui/icons-material';

interface TemplateUpdateConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  templateName?: string;
}

export const TemplateUpdateConfirmationDialog: React.FC<TemplateUpdateConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  templateName = 'Email Template',
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Update sx={{ color: 'primary.main', fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Update Email Template
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 5, pb: 1, px: 2.5, }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 ,}}>
          <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.5, mb: 1,mt:3 }}>
            You have made modifications to the <strong>{templateName}</strong> template. 
            <br />
            Please choose how you would like to proceed with these changes:
          </Typography>
          
          <Box sx={{ 
            backgroundColor: 'grey.50', 
            p: 1.5, 
            borderRadius: 1, 
            border: '1px solid', 
            borderColor: 'grey.200',
            mb: 1 
          }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              <strong>Note:</strong> Email templates can be previewed and customized before sending. 
              Changes can be saved permanently to the template for all future 
              communications.
            </Typography>
          </Box>
          
          <Box
            sx={{
              p: 2,
              backgroundColor: 'primary.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.200',
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'primary.dark', fontWeight: 600, mb: 2 }}>
              Available Options:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  backgroundColor: 'success.main', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 0.2
                }}>
                  <Save sx={{ color: 'white', fontSize: 14 }} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                    Save Changes Permanently
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
                    Apply modifications to the template permanently. These changes will be used for all future emails sent with this template.
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  backgroundColor: 'warning.main', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 0.2
                }}>
                  <Close sx={{ color: 'white', fontSize: 14 }} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                    Discard Changes
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
                    Discard all modifications and use the original template content. No changes will be saved to the template.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 2,
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          startIcon={<Close />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 2,
            px: 2.5,
            py: 1,
            borderColor: 'warning.main',
            color: 'warning.main',
            '&:hover': {
              borderColor: 'warning.dark',
              color: 'warning.dark',
              backgroundColor: 'warning.50',
            },
          }}
        >
          Discard Changes
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          startIcon={<Save />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 2,
            px: 2.5,
            py: 1,
            backgroundColor: 'success.main',
            '&:hover': {
              backgroundColor: 'success.dark',
            },
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateUpdateConfirmationDialog;
